import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings as SettingsIcon, Loader2, Percent } from "lucide-react";
import { toast } from "sonner";

export default function AdminSettings() {
  const [form, setForm] = useState({ host_fee_pct: 8, cleaner_fee_pct: 8 });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setForm({ host_fee_pct: r.data.host_fee_pct, cleaner_fee_pct: r.data.cleaner_fee_pct })); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", { host_fee_pct: Number(form.host_fee_pct), cleaner_fee_pct: Number(form.cleaner_fee_pct) });
      toast.success("Commission settings updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const example = 120;
  const hostCharge = example * (1 + Number(form.host_fee_pct) / 100);
  const cleanerNet = example * (1 - Number(form.cleaner_fee_pct) / 100);
  const platformCut = example * (Number(form.host_fee_pct) + Number(form.cleaner_fee_pct)) / 100;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-settings">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight text-sapphire">Platform Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">Configure your commission — applied automatically on every payout.</p>
      </div>

      <form onSubmit={save} className="border border-border rounded-md bg-white p-6 space-y-5" data-testid="settings-form">
        <div className="flex items-center gap-2 text-sapphire"><SettingsIcon className="w-5 h-5 text-ocean" /><h2 className="font-heading font-bold text-lg">Commission Rates</h2></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Host Service Fee (%)</Label>
            <div className="relative">
              <Input type="number" min={0} max={50} step="0.1" value={form.host_fee_pct} onChange={(e) => setForm({ ...form, host_fee_pct: e.target.value })} data-testid="host-fee" className="pr-8" />
              <Percent className="w-4 h-4 text-muted-foreground absolute right-2.5 top-2.5" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Added on top of the job value, charged to the host.</p>
          </div>
          <div>
            <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Cleaner Fee (%)</Label>
            <div className="relative">
              <Input type="number" min={0} max={50} step="0.1" value={form.cleaner_fee_pct} onChange={(e) => setForm({ ...form, cleaner_fee_pct: e.target.value })} data-testid="cleaner-fee" className="pr-8" />
              <Percent className="w-4 h-4 text-muted-foreground absolute right-2.5 top-2.5" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Deducted from the cleaner's payout.</p>
          </div>
        </div>

        <div className="border border-border rounded-md bg-slate-50 p-4">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Example on a $120 job</div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div><div className="text-muted-foreground text-xs">Host pays</div><div className="font-heading font-bold text-sapphire">${hostCharge.toFixed(2)}</div></div>
            <div><div className="text-muted-foreground text-xs">Cleaner gets</div><div className="font-heading font-bold text-emerald-700">${cleanerNet.toFixed(2)}</div></div>
            <div><div className="text-muted-foreground text-xs">You keep</div><div className="font-heading font-bold text-ocean">${platformCut.toFixed(2)}</div></div>
          </div>
        </div>

        <Button type="submit" disabled={saving} className="bg-ocean hover:bg-ocean/90" data-testid="settings-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Settings"}
        </Button>
      </form>
    </div>
  );
}
